/*
  /family/join?token=… — invite acceptance landing page.
  - Anonymous: prompts sign-in/register, then auto-accepts after auth.
  - Signed-in: previews the invite + offers Accept / Decline.
*/
import { useEffect, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { Users, CheckCircle2, Loader2, AlertTriangle } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { family as familyApi } from "../lib/accountApi";
import AuthForm from "../components/AuthForm";
import { toast } from "sonner";

export default function JoinFamilyPage() {
  const [sp] = useSearchParams();
  const token = sp.get("token") || "";
  const nav = useNavigate();
  const { user, loading } = useAuth();

  const [preview, setPreview] = useState(null);
  const [previewErr, setPreviewErr] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!token) return;
    familyApi.previewInvite(token)
      .then(setPreview)
      .catch((e) => setPreviewErr(e?.response?.data?.detail || "This invite is invalid"));
  }, [token]);

  async function accept() {
    setBusy(true);
    try {
      const fam = await familyApi.acceptInvite(token);
      toast.success(`Joined ${fam.name} 🎉`);
      nav("/family", { replace: true });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't accept invite");
    } finally { setBusy(false); }
  }

  if (!token) {
    return (
      <div className="max-w-md mx-auto py-8" data-testid="join-no-token">
        <h1 className="font-display tracking-tighter text-2xl mb-2">Invalid invite</h1>
        <p className="text-sm" style={{ color: "var(--text-muted)" }}>
          The link is missing its token. Please ask the inviter to resend.
        </p>
      </div>
    );
  }

  if (previewErr) {
    return (
      <div className="max-w-md mx-auto py-8 space-y-3" data-testid="join-invalid">
        <div className="flex items-start gap-3 rounded-lg p-3"
             style={{ background: "color-mix(in srgb, var(--error) 12%, transparent)", color: "var(--error)" }}>
          <AlertTriangle className="h-5 w-5 mt-0.5 shrink-0" />
          <div className="text-sm">{previewErr}</div>
        </div>
        <Link to="/" className="text-sm hover:underline" style={{ color: "var(--primary-ink)" }}>← Back to Hub</Link>
      </div>
    );
  }

  if (!preview || loading) {
    return <div className="py-12 flex justify-center"><Loader2 className="h-6 w-6 animate-spin" /></div>;
  }

  if (preview.accepted) {
    return (
      <div className="max-w-md mx-auto py-8" data-testid="join-already-accepted">
        <div className="flex items-start gap-3 rounded-lg p-3"
             style={{ background: "color-mix(in srgb, var(--success) 12%, transparent)", color: "var(--success)" }}>
          <CheckCircle2 className="h-5 w-5 mt-0.5 shrink-0" />
          <div className="text-sm">This invitation has already been used.</div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-md mx-auto py-8 space-y-5" data-testid="join-page">
      <div className="text-center">
        <div className="mx-auto h-14 w-14 rounded-2xl flex items-center justify-center mb-4"
             style={{ background: "var(--primary-soft)", color: "var(--primary-ink)" }}>
          <Users className="h-7 w-7" />
        </div>
        <h1 className="font-display tracking-tighter text-3xl mb-1">
          Join <span style={{ color: "var(--primary-ink)" }}>{preview.family_name}</span>
        </h1>
        <p className="text-sm" style={{ color: "var(--text-muted)" }}>
          Invited by {preview.inviter_name || preview.inviter_email} as <strong>{preview.role}</strong>.
        </p>
      </div>

      {!user ? (
        <div className="card-tactile rounded-2xl p-5">
          <p className="text-sm mb-4" style={{ color: "var(--text-muted)" }}>
            Sign in or create an account to accept the invitation. Your account will be linked to this family automatically.
          </p>
          <AuthForm initialMode="register" onDone={accept} />
        </div>
      ) : (
        <div className="card-tactile rounded-2xl p-5 space-y-3">
          <p className="text-sm" style={{ color: "var(--text-muted)" }}>
            Signed in as <strong>{user.email}</strong>.
          </p>
          <div className="flex gap-2">
            <button
              onClick={accept}
              disabled={busy}
              data-testid="join-accept"
              className="flex-1 inline-flex items-center justify-center gap-2 h-10 rounded-lg text-sm font-semibold text-white disabled:opacity-60"
              style={{ background: "var(--primary)" }}
            >
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
              Accept &amp; join
            </button>
            <Link
              to="/"
              data-testid="join-decline"
              className="inline-flex items-center justify-center h-10 px-4 rounded-lg text-sm font-semibold border"
              style={{ borderColor: "var(--border-subtle)", color: "var(--text-main)" }}
            >Not now</Link>
          </div>
        </div>
      )}
    </div>
  );
}
